<template lang="pug">
Publisher(
  v-if="initialized",
  ref="publisher",
  :isSysCheck="isSysCheck",
  :isBreakout="isBreakout",
  :mediaOnly="mediaOnly",
  :token="activeToken",
  @publishError="$emit('publishError', $event)"
)
</template>
<script lang="ts">
import config from "@/config";
import {
  ConnectionEvent,
  Group,
  StreamCreatedEvent,
  StreamDestroyedEvent,
  StreamPropertyChangedEvent,
  ToggleVideoUser,
  VideoDisabledWarningEvent,
  VideoDisabledWarningLiftedEvent,
  VideoElementCreatedEvent,
  VideoEnabledChangedEvent,
} from "@/types";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import Publisher from "./Publisher.vue";
import OT, { OTError, SubscriberStats } from "@opentok/client";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import authorization from "@/auth/authorization";
import VonageUtil from "./vonageUtil";
import {
  AddSubscriber,
  ClearSubscribers,
  MediaOnly,
  RemoveSubscriber,
  SetPublisher,
  SetSession,
  VideoContainerId,
} from "@/types/vonage";
import { SetMediaState } from "@/types/media";
import employeeLogo from "@/assets/images/logos/Crucial-C.svg";
import { EventContentModel } from "@cruciallearning/puddle";
import routes from "@/services/routes";
import { genericErrors } from "./ErrorHandler";
import log from "./DebugLogger";
import NetworkUtil from "@/utils/NetworkUtil";

@Component({
  components: {
    Publisher,
  },
  computed: {
    ...mapState([
      "streamSessionId",
      "streamToken",
      "breakoutVonageToken",
      "breakoutSessionId",
      "selectedVideoId",
      "sessionToggle",
    ]),
    ...mapState("EventModule", ["event"]),
    ...mapState("UsersModule", ["users"]),
    ...mapState("VonageModule", ["subscribers", "session", "publisher"]),
    ...mapGetters("BreakoutsModule", ["getActiveBreakoutSelf"]),
    ...mapGetters("EventModule", ["rtcStatsEnabled"]),
  },
  methods: {
    ...mapMutations("MediaModule", ["setMediaState"]),
    ...mapMutations("UsersModule", ["toggleVideoUser", "clearAllVideoUsers"]),
    ...mapActions("VonageModule", [
      "setPublisher",
      "setSession",
      "clearSubscribers",
      "addSubscriber",
      "removeSubscriber",
      "postRtcReport",
    ]),
  },
})
export default class Session extends Vue {
  @Prop({ required: false, default: false }) isSysCheck!: boolean;
  @Prop({ required: false, default: undefined }) mediaOnly!: MediaOnly | undefined;

  private readonly session!: OT.Session | null;
  private readonly subscribers!: OT.Subscriber[];
  private readonly publisher!: OT.Publisher | null;

  private readonly event!: EventContentModel;
  private readonly getActiveBreakoutSelf!: Group | undefined;

  private readonly streamSessionId!: string;
  private readonly streamToken!: string;
  private readonly breakoutVonageToken!: string;
  private readonly breakoutSessionId!: string;

  private readonly setMediaState!: SetMediaState;
  private readonly toggleVideoUser!: ToggleVideoUser;

  private readonly setPublisher!: SetPublisher;
  private readonly addSubscriber!: AddSubscriber;
  private readonly removeSubscriber!: RemoveSubscriber;
  private readonly setSession!: SetSession;
  private readonly clearSubscribers!: ClearSubscribers;
  private readonly rtcStatsEnabled!: boolean;
  private readonly postRtcReport!: () => void;

  private muteIconSrc!: string;
  initialized = false;

  async mounted(): Promise<void> {
    this.muteIconSrc = await VonageUtil.getMuteIconSrc();
    this.getToken();
    this.setStatsInterval();
  }

  beforeDestroy(): void {
    console.log("Destroyed component -- disconnecting from session");
    this.removeReconnectSessionOnceOnline();
    this.session?.disconnect();
  }
  async init(): Promise<void> {
    this.initialized = false;
    OT.on("exception", (event) => {
      log("Vonage exception", event);
    });
    const sessionLocal = OT.initSession(config.streamKey, this.activeSessionId);
    sessionLocal.on({
      connectionCreated: (event: ConnectionEvent) => {
        log("Connection Created", event);
      },
      connectionDestroyed: (event: ConnectionEvent) => {
        log("Connection Destroyed", event);
      },
      sessionConnected: (event: unknown) => {
        log("Session Connected", event);
      },
      sessionDisconnected: (event: { reason: string }) => {
        log("Session Disconnected; reason=" + event?.reason, event);
        if (event?.reason === "networkDisconnected") {
          log("Session Disconnected because networkDisconnected; will attempt to manually reconnect", event);
          this.reconnectSessionOnceOnline();
        }
      },
      sessionReconnected: (event: unknown) => {
        log("Session Reconnected", event);
      },
      sessionReconnecting: (event: unknown) => {
        log("Session Reconnecting...", event);
      },
      streamCreated: (event: StreamCreatedEvent) => {
        //This is fired when other users connect to you
        log("Stream Created", event);
        const idx = this.subscribers.findIndex((e) => e.stream?.streamId === event.stream?.streamId);
        if (idx == -1 && event.stream) {
          const subscriber = this.initSubscriber(event.stream);
          if (subscriber) this.addSubscriber(subscriber);
        }
      },
      streamDestroyed: (event: StreamDestroyedEvent) => {
        if (event?.reason === "networkDisconnected") {
          // see https://developer.vonage.com/en/video/guides/subscribe-streams/troubleshooting-js#losing-connectivity
          log(
            "Stream Destroyed with reason=networkDisconnected; preventing default with hope of eventual reconnect.",
            event
          );
          event.preventDefault();
        } else {
          log("Stream Destroyed with reason=" + event?.reason + "; removing subscriber", event);
          if (event.stream) {
            const securityId = VonageUtil.getSecurityId(event.stream);
            if (securityId) {
              this.toggleVideoUser(securityId);
            }
          }
          this.removeSubscriber(event.stream);
        }
      },
      streamPropertyChanged: (event: StreamPropertyChangedEvent) => {
        log("Stream Property Changed", event);
        const securityId = VonageUtil.getSecurityId(event.stream);
        if (securityId) {
          if (securityId == this.$auth.authUser.id) {
            if (event.changedProperty === "hasVideo") {
              this.setMediaState({ hasVideo: event.newValue });
            }
            if (event.changedProperty === "hasAudio") {
              this.setMediaState({ hasAudio: event.newValue });
            }
          }
          if (event.changedProperty === "hasAudio") {
            VonageUtil.setMuteIconState(securityId, !event.newValue);
          }
          if (event.changedProperty === "hasVideo") {
            this.toggleVideoUser(securityId);
          }
        }
      },
      muteForced: (event: unknown) => {
        log("Mute forced", event);
      },
    });
    this.setSession(sessionLocal);
    this.initialized = true;
  }
  initSubscriber(stream: OT.Stream): OT.Subscriber | undefined {
    log("init subscriber", stream);
    let id = "";
    if (this.isBreakout) {
      id = "breakout-users";
    } else {
      let group = VideoContainerId.LEARNERS;
      const securityId = JSON.parse(stream.connection.data).securityId;
      if (authorization.isTrainer(securityId, this.event)) {
        group = VideoContainerId.TRAINERS;
      }
      if (authorization.isModerator(securityId, this.event) || authorization.isObserver(securityId, this.event)) {
        group = VideoContainerId.MODERATORS;
      }
      id = `${group}-video-container`;
    }
    const options: OT.SubscriberProperties = {
      insertDefaultUI: true,
      showControls: true,
      insertMode: "append",
      fitMode: "cover",
      style: {
        audioBlockedDisplayMode: "off",
        audioLevelDisplayMode: "off",
        buttonDisplayMode: "off",
        nameDisplayMode: "off",
        videoDisabledDisplayMode: "auto",
      },
    };

    const _subscriber = this.session?.subscribe(stream, id, options, genericErrors);
    _subscriber?.on({
      videoElementCreated: (event: VideoElementCreatedEvent) => {
        log("Subscriber Video Element Created", event);
        const subscriber = event.target as OT.Subscriber;
        if (!subscriber.stream) return;
        const securityId = JSON.parse(subscriber.stream.connection.data).securityId;
        const node = VonageUtil.userNameNode(securityId, event.target.stream?.connection.data);
        const mutedNode = VonageUtil.mutedIndicatorNode(event.target.stream?.name, this.muteIconSrc);
        if (node) {
          event.target.element?.append(node);
          event.target.element?.append(mutedNode);
          VonageUtil.setMuteIconState(securityId, !event.target.stream?.hasAudio);
        }
        if (authorization.isObserver(securityId)) {
          const iconNode = VonageUtil.employeeIndicatorNode(securityId, employeeLogo);
          event.target.element?.append(iconNode);
        }
        if (!this.isBreakout) {
          if (subscriber.element) {
            const parsedSecurityId = VonageUtil.userClass(securityId);
            subscriber.element.classList.add("user-video", "speaker", `user-video-${parsedSecurityId}`);
            if (authorization.isRegisteredLearner(securityId, this.event)) {
              if (this.$auth.ADMIN) {
                const menu = VonageUtil.menuIconNode(securityId);
                subscriber.element.append(menu);
              } else {
                const chatIcon = VonageUtil.chatIconNode(securityId);
                subscriber.element.append(chatIcon);
              }
            } else {
              const chatIcon = VonageUtil.chatIconNode(securityId);
              subscriber.element.append(chatIcon);
            }
          }
          if (event.target.element) {
            event.target.element.style.width = "45%";
            event.target.element.style.height = "0";
            event.target.element.style.paddingBottom = "35%";
          }
        } else {
          if (event.target.element) {
            const initialNode = VonageUtil.hiddenVideoNode(event.target.stream?.connection.data);
            if (initialNode) {
              event.target.element.append(initialNode);
            }
          }
        }
        if (subscriber.stream?.hasVideo) {
          this.toggleVideoUser(securityId);
        }
        // disable the context menu
        if (event.element) {
          event.element.oncontextmenu = () => {
            return false;
          };
        }
      },

      streamCreated: (event: StreamCreatedEvent): void => {
        log("Subscriber Stream Created", event);
      },

      destroyed: (event: unknown): void => {
        log("Subscriber destroyed", event);
      },

      connected: (event: ConnectionEvent): void => {
        log("Subscriber connected", event);
      },

      disconnected: (event: ConnectionEvent): void => {
        log("Subscriber disconnected", event);
      },

      videoEnabled: (event: VideoEnabledChangedEvent): void => {
        log("Subscriber video enabled; reason=" + event?.reason, event);
      },

      videoDisabled: (event: VideoEnabledChangedEvent): void => {
        log("Subscriber video disabled; reason=" + event?.reason, event);
      },

      // note yes the event _class_ really is "…Disabled…" while the event _name_ is just "…Disable…"
      videoDisableWarning: (event: VideoDisabledWarningEvent): void => {
        log("Subscriber video disable warning; reason=" + event?.reason, event);
        if (event.target.stream) {
          const subId = JSON.parse(event.target.stream.connection.data).securityId;
          const sub = this.subscribers.find((sub) => {
            if (sub.stream) {
              const securityId = JSON.parse(sub.stream?.connection.data).securityId;
              return securityId === subId;
            }
          });
          const frameRate = sub?.stream?.frameRate;
          if (frameRate && frameRate > 1) {
            if (sub) {
              console.log("reducing subscriber frame rate from " + frameRate);
              sub.getStats((error?: OTError, stats?: SubscriberStats): void => {
                if (stats) console.warn(stats);
              });
              sub.setPreferredFrameRate(frameRate - 1);
            }
          }
        }
      },

      // note yes the event _class_ really is "…Disabled…" while the event _name_ is just "…Disable…"
      videoDisableWarningLifted: (event: VideoDisabledWarningLiftedEvent): void => {
        log("Subscriber video disable warning lifted; reason=" + event?.reason, event);
      },

      audioBlocked: (event: unknown): void => {
        log("Subscriber audio blocked", event);
      },

      audioUnblocked: (event: unknown): void => {
        log("Subscriber audio unblocked", event);
      },
    });
    return _subscriber;
  }
  @Watch("getActiveBreakoutSelf")
  getToken(): void {
    if (!this.getActiveBreakoutSelf) {
      this.$socket.send({ type: routes.VONAGE_TOKEN });
    } else {
      this.$socket.send({ type: routes.BREAKOUT_TOKEN });
    }
  }
  @Watch("streamSessionId")
  @Watch("breakoutSessionId")
  streamReady(sessionId: string): void {
    if (this.event.systemCheck) return;
    if (!sessionId) return;
    this.subscribers.forEach((sub) => {
      this.session?.unsubscribe(sub);
      sub.off();
    });
    this.clearSubscribers();
    if (this.publisher) {
      this.session?.unpublish(this.publisher);
      this.publisher.off();
      this.publisher.destroy();
      this.setPublisher(null);
    }
    this.session?.off();
    this.session?.disconnect();
    this.setSession(null);
    this.init();
  }

  get isBreakout(): boolean {
    return !!this.breakoutSessionId && !!this.breakoutVonageToken;
  }

  get activeSessionId(): string {
    return this.isBreakout ? this.breakoutSessionId : this.streamSessionId;
  }

  get activeToken(): string {
    return this.isBreakout ? this.breakoutVonageToken : this.streamToken;
  }

  /** wait until we're back online, then trigger a manual reconnect of the session */
  private reconnectSessionOnceOnline(): void {
    this.removeReconnectSessionOnceOnline();
    NetworkUtil.onceOnline(this.reconnectSession);
  }

  /** remove any previously registered {@link #reconnectSessionOnceOnline} listener */
  private removeReconnectSessionOnceOnline(): void {
    NetworkUtil.removeOnlineListener(this.reconnectSession);
  }

  /** trigger a manual reconnect of the session */
  private reconnectSession(): void {
    if (this.$refs?.publisher instanceof Publisher) {
      (this.$refs.publisher as Publisher).sessionUpdate();
    }
  }

  setStatsInterval(): void {
    if (this.rtcStatsEnabled) {
      setInterval(() => {
        if (this.publisher) {
          this.postRtcReport();
        }
      }, 1000 * 60 * 10);
    }
  }
}
</script>
